import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  getApiKeys, 
  createApiKey, 
  deleteApiKey, 
  getInvitations, 
  createInvitation, 
  deleteInvitation,
  getEmailStats,
  syncUsersFromCollection,
  getUserSyncStatus,
  getCommHubUserStats,
  queryCollection,
  bulkImportCommHubUsers,
  batchUpdateUsersOrganizationId,
  User
} from '../lib/api';
import { 
  Key,
  Plus,
  Trash,
  Copy,
  CaretDown,
  CaretRight,
  Bell,
  Code,
  UserPlus,
  EnvelopeSimple,
  Clock,
  Check,
  CheckCircle,
  X,
  ChartBar,
  PaperPlaneTilt,
  Warning,
  ShieldCheck,
  ArrowsClockwise,
  Users,
  Database,
  Buildings
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { PushNotificationSettings } from '../components/PushNotificationSettings';
import { TotpSettings } from '../components/TotpSettings';

// Collapsible Section Component
function CollapsibleSection({ 
  title, 
  icon: Icon, 
  children, 
  defaultOpen = false, 
  badge = null 
}: {
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-slate-600" />
          <span className="font-medium text-slate-900">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <CaretDown size={18} className="text-slate-400" />
        ) : (
          <CaretRight size={18} className="text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-slate-100">
          {children}
        </div>
      )}
    </div>
  );
}

interface ApiKeyData {
  id: string;
  name?: string;
  permissions?: string[];
  created_at?: string;
}

interface InvitationData {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  status?: string;
  created_at?: string;
}


// Component to fix organization_id for all users
function FixOrganizationSection() {
  const [fixing, setFixing] = useState(false);
  const [result, setResult] = useState<{ updated: number; failed: number } | null>(null);
  const [organizations, setOrganizations] = useState<Array<{ id: string; name?: string }>>([]);
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  const loadOrganizations = useCallback(async () => {
    try {
      const orgs = await queryCollection<{ id: string; name?: string }>('organizations');
      setOrganizations(orgs);
      if (orgs.length > 0) {
        setSelectedOrg(orgs[0].id);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const handleFix = async () => {
    if (!selectedOrg) {
      toast.error('Välj en förening först');
      return;
    }

    if (!window.confirm('Är du säker? Detta kommer uppdatera organization_id för alla användare.')) {
      return;
    }

    setFixing(true);
    try {
      const result = await batchUpdateUsersOrganizationId(selectedOrg);
      setResult(result);
      toast.success(`Klart! ${result.updated} användare uppdaterade`);
    } catch (error) {
      console.error('Fix failed:', error);
      toast.error('Kunde inte uppdatera användare');
    } finally {
      setFixing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <Buildings size={20} />
        Koppla användare till förening
      </h3>
      
      <p className="text-sm text-slate-600 mb-4">
        Uppdatera alla användare så att de är kopplade till rätt förening. 
        Detta behövs om användare visar "Okänd" under Förening.
      </p>

      <div className="flex items-center gap-4 mb-4">
        <Select value={selectedOrg} onValueChange={setSelectedOrg}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Välj förening" />
          </SelectTrigger>
          <SelectContent>
            {organizations.map(org => (
              <SelectItem key={org.id} value={org.id}>{org.name || org.id}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button onClick={handleFix} disabled={fixing || !selectedOrg}>
          {fixing ? 'Uppdaterar...' : 'Uppdatera alla användare'}
        </Button>
      </div>

      {result && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-sm text-emerald-800">
            <strong>{result.updated}</strong> användare uppdaterade
            {result.failed > 0 && <span className="text-red-600 ml-2">({result.failed} misslyckades)</span>}
          </p>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const [apiKeys, setApiKeys] = useState<ApiKeyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState<{ key?: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    permissions: ['read']
  });
  
  // Invitation state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invitations, setInvitations] = useState<InvitationData[]>([]);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'resident'
  });

  // Email stats state
  const [emailStats, setEmailStats] = useState<{
    total_sent?: number;
    this_week?: number;
    by_day?: Record<string, number>;
    summary?: {
      email_total?: number;
      email_success?: number;
      sms_total?: number;
      sms_success?: number;
      push_total?: number;
      push_success?: number;
    };
    daily?: Array<{
      date?: string;
      email?: number;
      sms?: number;
      push?: number;
    }>;
    days?: Array<{
      date?: string;
      email?: { total?: number; failed?: number };
      sms?: { total?: number; failed?: number };
      push?: { total?: number; failed?: number };
    }>;
  } | null>(null);
  const [emailStatsLoading, setEmailStatsLoading] = useState(false);

  const fetchKeys = async () => {
    try {
      const { data } = await getApiKeys();
      setApiKeys(data);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const { data } = await getInvitations();
      setInvitations(data);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    }
  };

  const fetchEmailStats = async () => {
    setEmailStatsLoading(true);
    try {
      const { data } = await getEmailStats();
      setEmailStats(data);
    } catch (error) {
      console.error('Failed to fetch email stats:', error);
    } finally {
      setEmailStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
    fetchInvitations();
  }, []);

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error('Ange ett namn för API-nyckeln');
      return;
    }
    
    try {
      const { data } = await createApiKey(formData);
      setNewKey({ key: (data as { key?: string }).key });
      fetchKeys();
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  const handleDelete = async (id: string | undefined) => {
    if (!id || !window.confirm('Är du säker? Denna åtgärd kan inte ångras.')) return;
    
    try {
      await deleteApiKey(id);
      toast.success('API-nyckel borttagen');
      fetchKeys();
    } catch (error) {
      toast.error(t('error_occurred'));
    }
  };

  const copyToClipboard = (text: string | undefined) => {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success('Kopierad till urklipp');
    }
  };

  const togglePermission = (perm: string) => {
    setFormData(prev => {
      const perms = prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm];
      return { ...prev, permissions: perms };
    });
  };

  const handleInvite = async () => {
    if (!inviteForm.email) {
      toast.error('E-postadress krävs');
      return;
    }
    
    setInviteLoading(true);
    try {
      const { data } = await createInvitation(inviteForm);
      toast.success('Inbjudan skickad');
      setInviteDialogOpen(false);
      setInviteForm({ email: '', name: '', role: 'resident' });
      fetchInvitations();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err.response?.data?.detail || 'Kunde inte skicka inbjudan');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = async (id: string | undefined) => {
    if (!id) return;
    try {
      await deleteInvitation(id);
      toast.success('Inbjudan borttagen');
      fetchInvitations();
    } catch (error) {
      toast.error('Kunde inte ta bort inbjudan');
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const roleLabels: Record<string, string> = {
    resident: 'Boende',
    admin: 'Admin',
    board_member: 'Styrelsemedlem',
    auditor: 'Revisor',
    suppliant: 'Suppleant'
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-4 max-w-3xl" data-testid="settings-page">
      <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 mb-6">
        {t('settings')}
      </h1>

      {/* User Invitations - Collapsible */}
      <CollapsibleSection 
        title="Bjud in användare" 
        icon={UserPlus}
        defaultOpen={true}
        badge={invitations.length > 0 ? `${invitations.length} väntande` : null}
      >
        <div className="pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Skicka inbjudningar via e-post till nya användare
            </p>
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="invite-user-button">
                  <UserPlus size={14} className="mr-1" />
                  Bjud in
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle className="text-base">Bjud in användare</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs">E-postadress *</Label>
                    <Input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="namn@exempel.se"
                      className="h-9"
                      data-testid="invite-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">Namn (valfritt)</Label>
                    <Input
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Förnamn Efternamn"
                      className="h-9"
                      data-testid="invite-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">Roll</Label>
                    <Select 
                      value={inviteForm.role} 
                      onValueChange={(v) => setInviteForm(prev => ({ ...prev, role: v }))}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resident">Boende</SelectItem>
                        <SelectItem value="board_member">Styrelsemedlem</SelectItem>
                        <SelectItem value="suppliant">Suppleant</SelectItem>
                        <SelectItem value="auditor">Revisor</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    onClick={handleInvite} 
                    className="w-full" 
                    size="sm" 
                    disabled={inviteLoading}
                    data-testid="send-invite-button"
                  >
                    {inviteLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Skickar...
                      </span>
                    ) : (
                      <>
                        <EnvelopeSimple size={14} className="mr-1" />
                        Skicka inbjudan
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Pending invitations list */}
          {invitations.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-600">Väntande inbjudningar</p>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
                {invitations.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-3"
                    data-testid={`invitation-${invite.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <Clock size={14} className="text-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {invite.name || invite.email}
                        </div>
                        <div className="text-[10px] text-slate-500 flex items-center gap-2">
                          <span>{invite.email}</span>
                          <span>•</span>
                          <span>{roleLabels[invite.role || ''] || invite.role}</span>
                          <span>•</span>
                          <span>{formatDate(invite.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvite(invite.id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                      title="Ta bort inbjudan"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Push Notifications - Collapsible */}
      <CollapsibleSection 
        title="Notifikationer" 
        icon={Bell}
      >
        <div className="pt-4">
          <p className="text-xs text-slate-500 mb-3">
            Pushnotiser skickar direktmeddelanden till din webbläsare när något händer i systemet, 
            t.ex. nya ärenden eller statusuppdateringar.
          </p>
          <PushNotificationSettings />
        </div>
      </CollapsibleSection>

      {/* Two-Factor Authentication - Collapsible */}
      <CollapsibleSection 
        title="Tvåfaktorsautentisering" 
        icon={ShieldCheck}
      >
        <div className="pt-4">
          <p className="text-xs text-slate-500 mb-3">
            Öka säkerheten för ditt konto genom att lägga till en extra autentiseringsmetod.
          </p>
          <TotpSettings />
        </div>
      </CollapsibleSection>

      {/* API Keys - Collapsible */}
      <CollapsibleSection 
        title="API-nycklar" 
        icon={Key}
        badge={apiKeys.length > 0 ? `${apiKeys.length} st` : null}
      >
        <div className="pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Hantera API-nycklar för externa integrationer
            </p>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setNewKey(null);
                setFormData({ name: '', permissions: ['read'] });
              }
            }}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="create-api-key-button">
                  <Plus size={14} className="mr-1" />
                  Skapa
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle className="text-base">
                    {newKey ? 'API-nyckel skapad' : 'Skapa API-nyckel'}
                  </DialogTitle>
                </DialogHeader>
                
                {newKey && newKey.key ? (
                  <div className="space-y-4 mt-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-xs text-amber-800">
                        <strong>Viktigt:</strong> Denna nyckel visas endast en gång.
                      </p>
                    </div>
                    <div className="relative">
                      <Input
                        value={newKey.key}
                        readOnly
                        className="font-mono text-xs pr-10"
                        data-testid="new-api-key-value"
                      />
                      <button
                        onClick={() => copyToClipboard(newKey.key)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                    <Button onClick={() => setDialogOpen(false)} className="w-full" size="sm">
                      Klar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Namn *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Min integration"
                        className="h-9"
                        data-testid="api-key-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs">Behörigheter</Label>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="read"
                            checked={formData.permissions.includes('read')}
                            onCheckedChange={() => togglePermission('read')}
                          />
                          <label htmlFor="read" className="text-xs text-slate-700">
                            Läsa (read)
                          </label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="write"
                            checked={formData.permissions.includes('write')}
                            onCheckedChange={() => togglePermission('write')}
                          />
                          <label htmlFor="write" className="text-xs text-slate-700">
                            Skriva (write)
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <Button onClick={handleCreate} className="w-full" size="sm" data-testid="confirm-create-key">
                      Skapa nyckel
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="animate-pulse">
              <div className="h-12 bg-slate-100 rounded" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">
              Inga API-nycklar skapade
            </div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between p-3"
                  data-testid={`api-key-row-${key.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded bg-slate-100 flex items-center justify-center">
                      <Key size={14} className="text-slate-500" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{key.name}</div>
                      <div className="text-[10px] text-slate-500">
                        {key.permissions?.join(', ') || '-'} • {key.created_at ? new Date(key.created_at).toLocaleDateString('sv-SE') : '-'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(key.id)}
                    className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                    data-testid={`delete-key-${key.id}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* API Documentation - Collapsible */}
      <CollapsibleSection title="API Dokumentation" icon={Code}>
        <div className="pt-4 space-y-4">
          <div>
            <h3 className="text-xs font-medium text-slate-700 mb-2">Endpoints</h3>
            <div className="font-mono text-[11px] space-y-1.5 p-3 bg-slate-50 rounded-md">
              <div><span className="text-green-600">GET</span> /api/external/issues?api_key=YOUR_KEY</div>
              <div><span className="text-blue-600">PUT</span> /api/external/issues/:id?api_key=YOUR_KEY</div>
            </div>
          </div>
          
          <div>
            <h3 className="text-xs font-medium text-slate-700 mb-2">Exempel</h3>
            <div className="font-mono text-[10px] p-3 bg-slate-900 text-slate-100 rounded-md overflow-x-auto">
              <pre className="whitespace-pre-wrap">{`curl -X GET "https://api.example.com/api/external/issues?api_key=YOUR_KEY"

curl -X PUT "https://api.example.com/api/external/issues/123?api_key=YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"status": "resolved"}'`}</pre>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Communication Statistics - Collapsible */}
      <CollapsibleSection 
        title="Kommunikationsstatistik" 
        icon={ChartBar}
        badge={emailStats ? `${(emailStats.summary?.email_total || 0) + (emailStats.summary?.sms_total || 0)} senaste 7 dagar` : null}
      >
        <div className="pt-4 space-y-4">
          {!emailStats ? (
            <div className="text-center py-4">
              <Button
                onClick={fetchEmailStats}
                disabled={emailStatsLoading}
                variant="outline"
                size="sm"
              >
                {emailStatsLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                    Laddar...
                  </span>
                ) : (
                  <>
                    <ChartBar size={16} className="mr-2" />
                    Visa statistik
                  </>
                )}
              </Button>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-700">{emailStats.summary?.email_total || 0}</div>
                  <div className="text-xs text-blue-600">E-post</div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <div className="text-2xl font-bold text-emerald-700">{emailStats.summary?.sms_total || 0}</div>
                  <div className="text-xs text-emerald-600">SMS</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <div className="text-2xl font-bold text-amber-700">{emailStats.summary?.push_total || 0}</div>
                  <div className="text-xs text-amber-600">Push</div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">
                    {(emailStats.summary?.email_success || 0) + (emailStats.summary?.sms_success || 0)}
                  </div>
                  <div className="text-xs text-green-600">Lyckade</div>
                </div>
              </div>

              {/* Channel breakdown */}
              <div className="space-y-3">
                <h3 className="text-xs font-medium text-slate-700">Per kanal (CommHub)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {/* Email */}
                  <div className="p-3 border border-blue-200 bg-blue-50/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <EnvelopeSimple size={16} className="text-blue-600" />
                      <span className="font-medium text-blue-900">E-post</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Totalt:</span>
                        <span className="font-medium">{emailStats.summary?.email_total || 0}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Lyckade:</span>
                        <span>{emailStats.summary?.email_success || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* SMS */}
                  <div className="p-3 border border-emerald-200 bg-emerald-50/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <PaperPlaneTilt size={16} className="text-emerald-600" />
                      <span className="font-medium text-emerald-900">SMS</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Totalt:</span>
                        <span className="font-medium">{emailStats.summary?.sms_total || 0}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Lyckade:</span>
                        <span>{emailStats.summary?.sms_success || 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Push */}
                  <div className="p-3 border border-amber-200 bg-amber-50/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell size={16} className="text-amber-600" />
                      <span className="font-medium text-amber-900">Push</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Totalt:</span>
                        <span className="font-medium">{emailStats.summary?.push_total || 0}</span>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <span>Lyckade:</span>
                        <span>{emailStats.summary?.push_success || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Daily breakdown */}
              <div className="space-y-2">
                <h3 className="text-xs font-medium text-slate-700">Senaste 7 dagarna</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 px-2 font-medium text-slate-600">Datum</th>
                        <th className="text-right py-2 px-2 font-medium text-blue-600">E-post</th>
                        <th className="text-right py-2 px-2 font-medium text-emerald-600">SMS</th>
                        <th className="text-right py-2 px-2 font-medium text-amber-600">Push</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailStats.days?.map((day, idx) => (
                        <tr key={day.date} className={idx === 0 ? 'bg-slate-50' : ''}>
                          <td className="py-2 px-2">
                            {idx === 0 ? (
                              <span className="font-medium text-slate-900">Idag</span>
                            ) : (
                              new Date(day.date || '').toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })
                            )}
                          </td>
                          <td className="text-right py-2 px-2 text-blue-600">
                            {day.email?.total || 0}
                            {(day.email?.failed ?? 0) > 0 && (
                              <span className="text-red-500 ml-1">({day.email?.failed} fel)</span>
                            )}
                          </td>
                          <td className="text-right py-2 px-2 text-emerald-600">
                            {day.sms?.total || 0}
                            {(day.sms?.failed ?? 0) > 0 && (
                              <span className="text-red-500 ml-1">({day.sms?.failed} fel)</span>
                            )}
                          </td>
                          <td className="text-right py-2 px-2 text-amber-600">
                            {day.push?.total || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Refresh button */}
              <div className="pt-2 border-t border-slate-100">
                <Button
                  onClick={fetchEmailStats}
                  disabled={emailStatsLoading}
                  variant="outline"
                  size="sm"
                >
                  {emailStatsLoading ? 'Uppdaterar...' : 'Uppdatera statistik'}
                </Button>
              </div>
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* User Migration - Completed */}
      <CollapsibleSection title="Användarmigrering till CommHub" icon={ArrowsClockwise}>
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle size={20} />
            <span className="font-medium">Migrering slutförd</span>
          </div>
          <p className="text-sm text-green-600 mt-2">
            Alla användare har migrerats till CommHub. Legacy-datan (forvaltaren_users) används inte längre.
          </p>
        </div>
      </CollapsibleSection>

      {/* Fix Organization Section */}
      <CollapsibleSection title="Koppla användare till förening" icon={Buildings}>
        <FixOrganizationSection />
      </CollapsibleSection>
    </div>
  );
}
